<template>
  <AuthenticatedView
    :loading="projectLoading && !projectResult"
    :current-user="currentUser"
    :has-result="!!projectResult"
  >
    <div>
      <AppHeader
        v-if="!embedded"
        :user="currentUser"
        :dashboard-url="dashboardUrl"
        :profile-url="profileUrl"
        :chat-id="chatId"
        :from-id="fromId"
      />

      <div :class="embedded ? 'dashboard-panel' : 'card'">
        <breadcrumbs v-if="!embedded" :items="breadcrumbsItems" />
        <div
          :class="embedded ? 'dashboard-panel-header' : ''"
          :style="
            embedded
              ? ''
              : 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px;'
          "
        >
          <h3 v-if="embedded" id="project-attachments-panel-title" class="dashboard-panel-title">
            {{ s__('Attachments|Project files') }}
          </h3>
          <h2 v-else style="margin: 0">{{ s__('Attachments|Project Attachments') }}</h2>
          <button
            v-if="isTelegramWebView()"
            type="button"
            class="webview-warning-btn-header"
            :title="
              s__(
                'Attachments|File attachments download may not properly work in WebView. Click for more information.'
              )
            "
            @click="showWebViewInfoModal = true"
          >
            <WarningIcon :size="18" />
          </button>
        </div>

        <div :class="{ 'dashboard-panel-body': embedded }">
          <div v-if="loading && !attachments.length" class="loading-state">
            {{ __('Loading…') }}
          </div>

          <div v-else-if="error" class="error">{{ error }}</div>

          <div v-else>
            <FileUploader
              v-if="projectResult?.project?.canUpdateProject"
              attachable-type="Project"
              :attachable-id="props.projectId"
              :project-id="props.projectId"
              class="project-file-uploader"
              @uploaded="handleProjectFilesUploaded"
              @error="handleProjectUploadError"
            />

            <!-- Attachments List -->
            <div v-if="attachments.length" class="attachments-list-section">
              <div class="attachments-header">
                <div class="attachments-header-left">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="paperclip-icon"
                  >
                    <path
                      d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551"
                    ></path>
                  </svg>
                  <span>{{ s__('Attachments|Attachments') }} ({{ totalCount }})</span>
                </div>
              </div>

              <AttachmentItemsList
                :attachments="attachments"
                :downloading="downloading"
                :sending-to-telegram="sendingToTelegram"
                :get-file-icon-class="getFileIconClass"
                :get-file-icon="getFileIcon"
                :is-image-attachment="isImageAttachment"
                :get-task-url="getTaskUrl"
                @view-image="openImageGallery"
                @download="handleDownload"
                @send-to-telegram="handleSendToTelegram"
                @delete="confirmDelete"
                @public-link-updated="handlePublicLinkUpdated"
              />

              <!-- Load More Button -->
              <div v-if="hasNextPage" class="load-more-section">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="loadingMore"
                  @click="loadMore"
                >
                  <span v-if="loadingMore">{{ __('Loading…') }}</span>
                  <span v-else>{{ s__('Attachments|Load more') }}</span>
                </button>
              </div>
            </div>

            <div v-else class="attachments-empty-hint">
              <span class="attachments-empty-hint-icon"><InfoIcon /></span>
              <p class="attachments-empty-hint-title">{{ s__('Attachments|No files yet.') }}</p>
              <p class="attachments-empty-hint-text">
                {{
                  s__(
                    'Attachments|Add files from task details or comments using the attachment upload control.'
                  )
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :visible="showConfirmModal"
      :title="s__('Attachments|Delete Attachment?')"
      :message="
        s__(
          'Attachments|Are you sure you want to delete this attachment? This action cannot be undone.'
        )
      "
      :confirm-text="s__('Attachments|Delete')"
      :cancel-text="s__('Attachments|Cancel')"
      @confirm="deleteAttachment"
      @cancel="cancelDelete"
    />

    <!-- Image Gallery Modal -->
    <ImageGalleryModal
      :visible="showImageGallery"
      :images="galleryImages"
      :initial-index="galleryInitialIndex"
      @close="closeImageGallery"
    />

    <AlertBanner
      v-if="downloadError"
      type="error"
      :title="s__('Attachments|Download Failed')"
      :message="downloadError"
      :dismissible="true"
      :duration="0"
      @dismiss="downloadError = null"
    />

    <AlertBanner
      v-if="sendToTelegramInfo"
      type="info"
      :title="s__('Attachments|Sending to Telegram')"
      :message="
        s__(
          'Attachments|Your attachment is being sent to your Telegram Direct Messages. This may take a few moments.'
        )
      "
      :dismissible="true"
      :duration="5000"
      @dismiss="sendToTelegramInfo = false"
    />

    <AlertBanner
      v-if="sendToTelegramError"
      type="error"
      :title="s__('Attachments|Send to Telegram Failed')"
      :message="sendToTelegramError"
      :dismissible="true"
      :duration="0"
      @dismiss="sendToTelegramError = null"
    />

    <!-- WebView Info Modal -->
    <AttachmentsWebViewInfoModal
      :visible="showWebViewInfoModal"
      @close="showWebViewInfoModal = false"
    />
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, __, loadTranslations } from '@/locale'
import AlertBanner from '../common/AlertBanner.vue'
import Breadcrumbs from '../common/Breadcrumbs.vue'
import WarningIcon from '../common/icons/WarningIcon.vue'
import InfoIcon from '../common/icons/InfoIcon.vue'
import ConfirmModal from '../common/ConfirmModal.vue'
import ImageGalleryModal from '../common/ImageGalleryModal.vue'
import AttachmentsWebViewInfoModal from '../common/AttachmentsWebViewInfoModal.vue'
import AttachmentItemsList from '../common/AttachmentItemsList.vue'
import FileUploader from '../common/FileUploader.vue'
import AppHeader from '../common/AppHeader.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { useAttachmentList } from '@/composables/useAttachmentList'
import { useAttachmentDownload } from '@/composables/useAttachmentDownload'

const props = defineProps({
  projectId: String,
  chatId: String,
  fromId: String,
  embedded: {
    type: Boolean,
    default: false,
  },
})

const loading = ref(true)
const error = ref('')
const attachments = ref([])
const totalCount = ref(0)
const hasNextPage = ref(false)
const endCursor = ref(null)
const loadingMore = ref(false)
const localeKey = ref(0)

// Download composable for iOS support
const { downloading, downloadError, downloadAttachment, isTelegramWebView } =
  useAttachmentDownload()

// Send to Telegram state
const sendingToTelegram = ref(false)
const sendToTelegramError = ref(null)
const sendToTelegramInfo = ref(false)

// WebView info modal state
const showWebViewInfoModal = ref(false)

// Use composable for shared attachment list logic
const {
  showConfirmModal,
  showImageGallery,
  galleryImages,
  galleryInitialIndex,
  isImageAttachment,
  openImageGallery: openImageGalleryBase,
  closeImageGallery,
  confirmDelete,
  cancelDelete,
  deleteAttachment: deleteAttachmentBase,
  getFileIconClass,
  getFileIcon,
  getTaskUrl: getTaskUrlBase,
} = useAttachmentList({
  onDelete: deletedAttachment => {
    // Remove from local list
    const index = attachments.value.findIndex(a => a.id === deletedAttachment.id)
    if (index !== -1) {
      attachments.value.splice(index, 1)
      totalCount.value = Math.max(0, totalCount.value - 1)
    }
  },
})

// Wrapper methods that use the composable with component-specific data
const openImageGallery = attachment => {
  openImageGalleryBase(attachment, attachments.value)
}

const SEND_TO_TELEGRAM_MUTATION = gql`
  mutation SendAttachmentToTelegram($id: ID!) {
    sendAttachmentToTelegram(id: $id) {
      success
      errors
    }
  }
`

const { mutate: sendToTelegramMutation } = useMutation(SEND_TO_TELEGRAM_MUTATION)

const handleDownload = async attachment => {
  await downloadAttachment(attachment.downloadUrl, attachment.filename)
}

const handleSendToTelegram = async attachment => {
  if (!attachment) return

  sendingToTelegram.value = true
  sendToTelegramError.value = null
  sendToTelegramInfo.value = false

  try {
    const { data } = await sendToTelegramMutation({ id: attachment.id })

    if (data?.sendAttachmentToTelegram?.errors?.length) {
      // Error occurred (rate limit, missing file, authorization, etc.) - show error immediately
      const errorMsg = data.sendAttachmentToTelegram.errors.join(', ')
      sendToTelegramError.value = s__('Attachments|Failed to send to Telegram: %{error}').replace(
        '%{error}',
        errorMsg
      )
      sendToTelegramInfo.value = false
    } else if (data?.sendAttachmentToTelegram?.success) {
      // Job was successfully enqueued - show info banner
      sendToTelegramInfo.value = true
      // Hide info banner after a short delay
      setTimeout(() => {
        sendToTelegramInfo.value = false
      }, 5000)
    }
  } catch (error) {
    console.error('Send to Telegram error:', error)
    sendToTelegramError.value = s__('Attachments|Failed to send to Telegram')
    sendToTelegramInfo.value = false
  } finally {
    sendingToTelegram.value = false
  }
}

const deleteAttachment = async () => {
  try {
    await deleteAttachmentBase()
  } catch (err) {
    error.value = err.message || s__('Attachments|Failed to delete attachment')
  }
}

const handleProjectFilesUploaded = async () => {
  await refetchProject()
}

const handleProjectUploadError = errors => {
  error.value = Array.isArray(errors) ? errors.join(', ') : String(errors)
}

const handlePublicLinkUpdated = ({ attachmentId, publicLink }) => {
  const attachment = attachments.value.find(item => item.id === attachmentId)
  if (attachment) {
    attachment.publicLink = publicLink
  }
}

const getTaskUrl = task => {
  return getTaskUrlBase(task, props.chatId, props.fromId)
}

// GraphQL queries
const PROJECT_Q = gql`
  query GetProjectAttachments($projectId: ID!, $first: Int, $after: String) {
    project(id: $projectId) {
      id
      title
      preferredLanguage
      canUpdateProject
      attachments(first: $first, after: $after) {
        edges {
          node {
            id
            filename
            byteSize
            contentType
            createdAt
            downloadUrl
            humanSize
            canCreatePublicLink
            canManagePublicLink
            publicLink {
              id
              expiresAt
              passwordProtected
              publicUrl
              revoked
              expired
            }
            parentTask {
              id
              iid
              title
              projectId
            }
            parentTasks {
              id
              iid
              title
              projectId
            }
          }
          cursor
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
    currentUser {
      id
      name
      username
    }
  }
`

const {
  result: projectResult,
  loading: projectLoading,
  error: projectError,
  fetchMore: fetchMoreProject,
  refetch: refetchProject,
} = useQuery(PROJECT_Q, () => ({ projectId: props.projectId, first: 50, after: null }), {
  enabled: computed(() => !!props.projectId),
  fetchPolicy: 'network-only',
})

// Current user for AppHeader
const currentUser = computed(() => {
  return projectResult.value?.currentUser
})

watch(
  projectLoading,
  val => {
    loading.value = val
    // If loading is false and we have a result, remove the initial loading screen
    if (!val && projectResult.value) {
      removeInitialLoading()
    }
  },
  { immediate: true }
)

watch(loading, isLoading => {
  if (!isLoading && projectResult.value) {
    removeInitialLoading()
  }
})

watch(
  projectError,
  val => {
    if (val) {
      console.error('ProjectAttachments query error:', val)
      error.value = val.message || 'Failed to load attachments'
      removeInitialLoading()
    }
  },
  { immediate: true }
)

watch(
  projectResult,
  data => {
    if (data) {
      // We have a result, remove loading screen
      removeInitialLoading()
    }

    const projectData = data?.project
    if (projectData) {
      const attachmentsData = projectData.attachments
      if (attachmentsData) {
        attachments.value = attachmentsData.edges.map(edge => edge.node)
        hasNextPage.value = attachmentsData.pageInfo.hasNextPage
        endCursor.value = attachmentsData.pageInfo.endCursor
        // Show loaded count (totalCount is not available on connection type)
        totalCount.value = attachments.value.length

        // Load translations for the project's preferred language
        if (projectData.preferredLanguage) {
          document.documentElement.lang = projectData.preferredLanguage
          loadTranslations(projectData.preferredLanguage)
          localeKey.value++ // Force re-render
        }
      } else {
        // No attachments data - might be empty or authorization issue
        attachments.value = []
        hasNextPage.value = false
        endCursor.value = null
        totalCount.value = 0
      }
    }
  },
  { immediate: true }
)

// Debug: log query state to help diagnose infinite loader
watch(
  [projectLoading, projectResult, projectError, () => props.projectId],
  ([loading, result, err, projectId]) => {
    console.log('ProjectAttachments query state:', {
      loading,
      hasResult: !!result,
      hasError: !!err,
      projectId,
      project: result?.project,
      attachments: result?.project?.attachments,
    })
  },
  { immediate: true }
)

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const profileUrl = computed(() => {
  let url = '/profile'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const projectTasksUrl = computed(() => {
  let url = `/projects/${props.projectId}/dashboard`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const breadcrumbsItems = computed(() => {
  localeKey.value // eslint-disable-line no-unused-expressions
  return [
    { label: s__('Dashboard|Main'), url: dashboardUrl.value },
    {
      label: projectResult.value?.project?.title || s__('ProjectSettings|Project'),
      url: projectTasksUrl.value,
    },
    { label: s__('Attachments|Project Attachments') },
  ]
})

const loadMore = async () => {
  if (loadingMore.value || !hasNextPage.value || !endCursor.value) return

  loadingMore.value = true
  try {
    const result = await fetchMoreProject({
      variables: {
        projectId: props.projectId,
        first: 50,
        after: endCursor.value,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousProject = previousResult.project
        const moreProject = fetchMoreResult.project

        if (!moreProject?.attachments) return previousResult

        const previousEdges = previousProject.attachments.edges || []
        const newEdges = moreProject.attachments.edges || []
        const pageInfo = moreProject.attachments.pageInfo

        const updated = {
          ...previousResult,
          project: {
            ...previousProject,
            attachments: {
              ...moreProject.attachments,
              edges: [...previousEdges, ...newEdges],
              pageInfo,
            },
          },
        }

        // Update local state from the merged result
        if (updated.project?.attachments) {
          attachments.value = updated.project.attachments.edges.map(edge => edge.node)
          hasNextPage.value = updated.project.attachments.pageInfo?.hasNextPage || false
          endCursor.value = updated.project.attachments.pageInfo?.endCursor || null
          // Show loaded count (totalCount is not available on connection type)
          totalCount.value = attachments.value.length
        }

        return updated
      },
    })
  } catch (err) {
    console.error('Failed to load more attachments:', err)
    error.value = s__('Attachments|Failed to load more attachments')
  } finally {
    loadingMore.value = false
  }
}
</script>

<style src="../../styles/attachments_list.css"></style>
<style scoped>
.card {
  max-width: 1200px;
  margin: 24px auto;
  padding: 24px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.loading-state {
  text-align: center;
  padding: 40px;
  color: #6b7280;
}

.error {
  color: #dc2626;
  font-size: 14px;
  padding: 16px;
  background: #fee2e2;
  border-radius: 8px;
  margin-bottom: 16px;
}

.attachments-list-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.project-file-uploader {
  margin-bottom: 16px;
}

.attachments-empty-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #dbeafe;
  color: #1e3a8a;
}

.attachments-empty-hint-icon {
  width: 20px;
  height: 20px;
}

.attachments-empty-hint-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.attachments-empty-hint-text {
  margin: 0;
  font-size: 13px;
  text-align: center;
}

.webview-warning-btn-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 6px;
  color: #92400e;
  cursor: pointer;
  transition: all 0.2s;
}

.webview-warning-btn-header:hover {
  background: #fde68a;
  border-color: #f59e0b;
}

.webview-warning-btn-header:active {
  background: #fcd34d;
}

.webview-warning-btn-header svg {
  color: #d97706;
}
</style>
